import React, { useState, useRef, useEffect } from 'react';
import { uploadAPI, imageAPI, shirtProductAPI } from '../services/api';

const ShirtUpload = () => {
  const [shirts, setShirts] = useState([]);
  const [existingShirts, setExistingShirts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadExistingShirts();
  }, []);

  const loadExistingShirts = async () => {
    try {
      setLoading(true);
      const response = await imageAPI.listShirts();
      setExistingShirts(response.data.shirts || []);
    } catch (error) {
      console.error('Error loading existing shirts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setShirts(prev => [...prev, {
          id: Date.now() + Math.random(),
          url: e.target.result,
          file: file,
          name: '' // Initialize with empty name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await shirtProductAPI.syncFromUploadedImages();
      const { created, skipped, total } = response.data;
      alert(`Sync complete: ${created} added, ${skipped} already existed (${total} shirt images scanned).`);
      await loadExistingShirts();
    } catch (error) {
      console.error('Error syncing shirt products:', error);
      alert(error.response?.data?.error || 'Failed to sync shirt products. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleRemoveShirt = (id) => {
    setShirts(prev => prev.filter(shirt => shirt.id !== id));
  };

  const handleShirtNameChange = (id, name) => {
    setShirts(prev => prev.map(shirt => 
      shirt.id === id ? { ...shirt, name } : shirt
    ));
  };

  const handleUpload = async () => {
    if (shirts.length === 0) {
      return;
    }

    setUploading(true);

    try {
      // Upload each shirt to S3 and confirm with image_type='shirt'
      const uploadedShirts = await Promise.all(
        shirts.map(async (shirt) => {
          try {
            // Request presigned URL from backend with image_type='shirt'
            const response = await uploadAPI.getPresignedUrl(
              shirt.file.name,
              shirt.file.type,
              'shirt' // This will generate S3 path as shirt/{uuid}.{ext}
            );

            const { presigned_url, file_key } = response.data;

            // Upload file to S3 using presigned URL
            await uploadAPI.uploadToS3(presigned_url, shirt.file);

            // Confirm upload to save metadata in database with image_type='shirt'
            const confirmResponse = await uploadAPI.confirmUpload(
              file_key,
              shirt.file.name,
              shirt.file.type,
              'shirt', // Mark as shirt design
              shirt.name || shirt.file.name // Use custom name or fallback to filename
            );

            return {
              ...shirt,
              fileKey: file_key,
              uploadUUID: confirmResponse.data.upload_uuid,
              uploaded: true,
            };
          } catch (error) {
            console.error('Error uploading shirt:', error);
            throw new Error(`Failed to upload ${shirt.file.name}`);
          }
        })
      );

      alert(`Successfully uploaded ${uploadedShirts.length} shirt design(s)!`);
      setShirts([]);
      
      // Reload existing shirts
      await loadExistingShirts();
    } catch (error) {
      alert(error.message || 'Failed to upload shirts. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
      <h1 style={{ fontSize: '32px', marginBottom: '10px', textAlign: 'center' }}>
        Upload Shirt Designs
      </h1>
      <p style={{ textAlign: 'center', color: '#718096', marginBottom: '40px' }}>
        Upload shirt design images to be available for selection in Step 2
      </p>

      <div
        style={{
          border: '2px dashed #cbd5e0',
          borderRadius: '12px',
          padding: '60px 40px',
          textAlign: 'center',
          cursor: 'pointer',
          background: '#f7fafc',
          transition: 'all 0.3s',
          marginBottom: '30px'
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>👕</div>
        <h3 style={{ marginBottom: '10px' }}>Upload Shirt Design Images</h3>
        <p style={{ color: '#718096' }}>Click to browse or drag and drop</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>

      {shirts.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px' }}>
            Selected Shirts ({shirts.length})
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            {shirts.map(shirt => (
              <div
                key={shirt.id}
                style={{
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <img
                  src={shirt.url}
                  alt="Shirt preview"
                  style={{
                    width: '100%',
                    height: '280px', // Increased height for vertical rectangle
                    objectFit: 'cover'
                  }}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveShirt(shirt.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    fontSize: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
                <div style={{
                  padding: '15px',
                  background: 'white'
                }}>
                  <input
                    type="text"
                    placeholder="Enter shirt name..."
                    value={shirt.name}
                    onChange={(e) => handleShirtNameChange(shirt.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #cbd5e0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: '#a0aec0'
                  }}>
                    {shirt.file.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {shirts.length > 0 && (
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '16px 48px',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.6 : 1,
              transition: 'all 0.3s'
            }}
          >
            {uploading ? 'Uploading...' : `Upload ${shirts.length} Shirt${shirts.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      <div style={{
        marginTop: '40px',
        padding: '20px',
        background: '#f0fdf4',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#166534', fontSize: '14px' }}>
          💡 <strong>Note:</strong> These shirt designs will be available for users to select in Step 2 of the main workflow.
        </p>
      </div>

      {/* Existing Shirts Section */}
      <div style={{ marginTop: '60px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px' 
        }}>
          <h2 style={{ fontSize: '24px' }}>
            Uploaded Shirts in Database ({existingShirts.length})
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleSync}
              disabled={syncing}
              title="Copy any uploaded_images rows with image_type='shirt' into shirt_products"
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: syncing ? 'not-allowed' : 'pointer',
                opacity: syncing ? 0.6 : 1,
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {syncing ? '⏳ Syncing...' : '⟲ Sync to Shirt Products'}
            </button>
            <button
              onClick={loadExistingShirts}
              disabled={loading}
              style={{
                padding: '8px 16px',
                background: '#f7fafc',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? '↻ Refreshing...' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {loading && existingShirts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            <p>Loading existing shirts...</p>
          </div>
        ) : existingShirts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px',
            background: '#f7fafc',
            borderRadius: '12px',
            color: '#718096'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>👕</div>
            <h3 style={{ marginBottom: '10px', color: '#1a202c' }}>No shirt designs in database</h3>
            <p>Upload your first shirt design above to get started!</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            {existingShirts.map((shirt) => (
              <div
                key={shirt.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'box-shadow 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '320px', // Increased height for vertical rectangle
                    backgroundImage: `url(${shirt.s3_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: '#f7fafc'
                  }}
                />
                <div style={{ padding: '15px' }}>
                  <h4
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#1a202c'
                    }}
                    title={shirt.name || shirt.file_name}
                  >
                    {shirt.name || shirt.file_name}
                  </h4>
                  {shirt.name && (
                    <p style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '5px' }}>
                      📄 {shirt.file_name}
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: '#718096', marginBottom: '5px' }}>
                    📅 {new Date(shirt.created_at).toLocaleDateString()}
                  </p>
                  <p style={{ fontSize: '11px', color: '#a0aec0', fontFamily: 'monospace' }}>
                    ID: {shirt.uuid.substring(0, 8)}...
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShirtUpload;
